import { useEffect, useMemo } from 'react';
import { applyCookieMode } from '@/shared/api/authBridge';
import { marketApi, type HistoricoParams } from '@/shared/api/services/marketApi';
import { isOk, type ApiResult } from '@/shared/api/types';
import { filtrarPorIntereses } from '@/shared/domain/series';
import {
  CAPACIDADES_BASICAS,
  type Capacidades,
  type Fuente,
  type Historico,
  type Precio,
  type RubroInfo,
  type TasaBcv,
  type UltimosData,
} from '@/shared/domain/types';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { saveSnapshot, type MercadoKey } from '@/shared/store/slices/mercadoSlice';
import { useApiQuery, type QueryOptions, type QueryState } from './useApi';

/** Hooks de datos públicos de mercado. Pintan el snapshot guardado al instante y revalidan en segundo plano. */
const MIN = 60_000;

/** Qué hacer con una consulta "ligera": guardar la última respuesta buena para pintar sin red. */
function useSnapshotDe<T>(key: MercadoKey): Pick<QueryOptions<T>, 'initialData' | 'onSuccess'> {
  const dispatch = useAppDispatch();
  const guardado = useAppSelector((s) => s.mercado[key]);
  return {
    initialData: guardado ? (guardado.data as T) : undefined,
    onSuccess: (data: T) => {
      dispatch(saveSnapshot({ key, data }));
    },
  };
}

export function useUltimos(options: QueryOptions<UltimosData> = {}): QueryState<UltimosData> {
  const snap = useSnapshotDe<UltimosData>('ultimos');
  return useApiQuery('ultimos', (signal) => marketApi.getUltimos({}, { signal }), {
    staleMs: 2 * MIN,
    ...snap,
    ...options,
  });
}

export function useBcv(options: QueryOptions<TasaBcv> = {}): QueryState<TasaBcv> {
  const snap = useSnapshotDe<TasaBcv>('bcv');
  return useApiQuery('bcv', (signal) => marketApi.getBcv(undefined, { signal }), {
    staleMs: 5 * MIN,
    ...snap,
    ...options,
  });
}

export interface TasaActual {
  /** Bs por USD (null mientras no hay ningún dato). */
  tasa: number | null;
  fecha: string | null;
  isLoading: boolean;
}

/** Tasa BCV vigente: la que viene con los precios y, si no hay, la de /v1/bcv. */
export function useTasaActual(): TasaActual {
  const ultimos = useUltimos();
  const bcv = useBcv({ enabled: ultimos.data === undefined });
  if (ultimos.data)
    return { tasa: ultimos.data.tasaBcvActual, fecha: ultimos.data.tasaBcvFecha, isLoading: false };
  if (bcv.data) return { tasa: bcv.data.usd, fecha: bcv.data.fecha, isLoading: false };
  return { tasa: null, fecha: null, isLoading: ultimos.isLoading || bcv.isLoading };
}

export function useRubros(options: QueryOptions<RubroInfo[]> = {}): QueryState<RubroInfo[]> {
  const snap = useSnapshotDe<RubroInfo[]>('rubros');
  return useApiQuery('rubros', (signal) => marketApi.getRubros({ signal }), {
    staleMs: 30 * MIN,
    ...snap,
    ...options,
  });
}

export function useFuentes(options: QueryOptions<Fuente[]> = {}): QueryState<Fuente[]> {
  const snap = useSnapshotDe<Fuente[]>('fuentes');
  return useApiQuery('fuentes', (signal) => marketApi.getFuentes({ signal }), {
    staleMs: 30 * MIN,
    ...snap,
    ...options,
  });
}

export interface FiltroHistorico extends HistoricoParams {
  /** Filtra en el cliente: la API no filtra el histórico por región. */
  region?: string | null;
}

/** Histórico de una serie. La región se filtra en el cliente. */
export function useHistorico(
  filtro: FiltroHistorico | null,
  options: QueryOptions<Historico> = {},
): QueryState<Historico> {
  const { region, ...params } = filtro ?? { rubro: '' };
  const key = filtro ? `historico:${JSON.stringify(params)}:${region ?? ''}` : null;
  return useApiQuery(
    key,
    async (signal): Promise<ApiResult<Historico>> => {
      const r = await marketApi.getHistorico(params, { signal });
      if (!isOk(r) || !region) return r;
      return {
        ...r,
        data: { ...r.data, puntos: r.data.puntos.filter((p) => p.region === region) },
      };
    },
    { staleMs: 5 * MIN, ...options },
  );
}

export function useBcvHistorico(
  desde?: string,
  options: QueryOptions<TasaBcv[]> = {},
): QueryState<TasaBcv[]> {
  return useApiQuery(
    `bcv-historico:${desde ?? ''}`,
    (signal) => marketApi.getBcvHistorico(desde, { signal }),
    { staleMs: 10 * MIN, ...options },
  );
}

/** Los errores que significan "esta API no tiene el add-on": se cae al modo básico sin avisar. */
const SIN_ADDON = new Set([404, 405, 501]);

/**
 * Capacidades del servidor. NUNCA devuelve undefined: mientras carga, o si el add-on no existe,
 * entrega `CAPACIDADES_BASICAS`. Además sincroniza el modo cookie del cliente HTTP.
 */
export function useCapacidades(): { capacidades: Capacidades; isLoading: boolean } {
  const snap = useSnapshotDe<Capacidades>('capacidades');
  const q = useApiQuery(
    'capacidades',
    async (signal): Promise<ApiResult<Capacidades>> => {
      const r = await marketApi.getCapacidades({ signal });
      if (isOk(r)) return r;
      const sinAddon = SIN_ADDON.has(r.status) || r.code === 'respuesta_invalida';
      return sinAddon ? { ok: true, data: CAPACIDADES_BASICAS } : r;
    },
    { staleMs: 30 * MIN, ...snap },
  );
  const capacidades = q.data ?? CAPACIDADES_BASICAS;
  const cookieMode = capacidades.auth.cookieMode;
  useEffect(() => {
    applyCookieMode(cookieMode);
  }, [cookieMode]);
  return { capacidades, isLoading: q.isLoading };
}

/** Precios de /ultimos limitados a los intereses de las preferencias (rubros, regiones, fuentes). */
export function usePreciosFiltrados(): { precios: Precio[]; todos: Precio[]; isLoading: boolean } {
  const ultimos = useUltimos();
  const intereses = useAppSelector((s) => s.prefs.config.intereses);
  const todos = useMemo(() => ultimos.data?.precios ?? [], [ultimos.data]);
  const precios = useMemo(() => filtrarPorIntereses(todos, intereses), [todos, intereses]);
  return { precios, todos, isLoading: ultimos.isLoading };
}
